/**
 * Связи объекта с организациями (KLS): список + добавление через боковую панель (Sheet).
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import { objectsApi, organizationsApi } from "@/services/api-v2.service";
import { klsSectionsApi } from "@/services/kls-api.service";
import type {
  ObjectOrganizationLink,
  ObjectOrganizationLinkCreate,
  OrganizationRelationshipRole,
} from "@/types/api-v2";
import { OrganizationKlsCombobox } from "@/components/admin/OrganizationKlsCombobox";
import { OrganizationRoleKlsCombobox } from "@/components/OrganizationRoleKlsCombobox";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Plus, Building2, UserCog, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export function ObjectOrganizationLinksPanel({ objectId }: { objectId: number }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [orgKlsId, setOrgKlsId] = useState<number | "">("");
  const [roleKlsId, setRoleKlsId] = useState<number | "">("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [linkIdToDelete, setLinkIdToDelete] = useState<number | null>(null);

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["object-org-links", objectId],
    queryFn: () => objectsApi.getOrganizationLinks(objectId),
  });

  const { data: roleCatalog = [] } = useQuery({
    queryKey: ["organizations", "relationship-roles"],
    queryFn: () => organizationsApi.listRelationshipRoles(),
    staleTime: 120_000,
  });

  const roleById = useMemo(() => {
    const m = new Map<number, OrganizationRelationshipRole>();
    roleCatalog.forEach((r) => m.set(r.id, r));
    return m;
  }, [roleCatalog]);

  const uniqueOrgIds = useMemo(
    () => [...new Set(links.map((l) => l.organization_kls_id))],
    [links],
  );

  const orgQueries = useQueries({
    queries: uniqueOrgIds.map((id) => ({
      queryKey: ["kls", "by-id", id] as const,
      queryFn: () => klsSectionsApi.get(id),
      staleTime: 60_000,
    })),
  });

  const orgIdToLabel = useMemo(() => {
    const m = new Map<number, { primary: string; code: string }>();
    uniqueOrgIds.forEach((id, i) => {
      const k = orgQueries[i]?.data;
      if (k) {
        m.set(id, {
          primary: k.kls_names?.trim() || k.kls_namef || String(id),
          code: k.kls_code || "—",
        });
      }
    });
    return m;
  }, [uniqueOrgIds, orgQueries]);

  const addMutation = useMutation({
    mutationFn: (body: ObjectOrganizationLinkCreate) =>
      objectsApi.createOrganizationLink(objectId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["object-org-links", objectId] });
      toast({ title: "Связь добавлена" });
      setOrgKlsId("");
      setRoleKlsId("");
      setSheetOpen(false);
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const delMutation = useMutation({
    mutationFn: (linkId: number) =>
      objectsApi.deleteOrganizationLink(objectId, linkId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["object-org-links", objectId] });
      toast({ title: "Удалено" });
    },
    onError: (e: Error) =>
      toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const handleSheetOpenChange = (open: boolean) => {
    setSheetOpen(open);
    if (!open) {
      setOrgKlsId("");
      setRoleKlsId("");
    }
  };

  const openAddSheet = () => {
    setOrgKlsId("");
    setRoleKlsId("");
    setSheetOpen(true);
  };

  const handleConfirmDeleteLink = () => {
    if (linkIdToDelete == null) return;
    const id = linkIdToDelete;
    setLinkIdToDelete(null);
    delMutation.mutate(id);
  };

  const handleAdd = () => {
    if (orgKlsId === "" || (typeof orgKlsId === "number" && orgKlsId <= 0)) {
      toast({
        title: "Выберите организацию",
        description: "Укажите организацию из справочника KLS.",
        variant: "destructive",
      });
      return;
    }
    const rid =
      roleKlsId === "" || (typeof roleKlsId === "number" && roleKlsId <= 0)
        ? null
        : roleKlsId;
    addMutation.mutate({
      organization_kls_id: orgKlsId as number,
      role_kls_id: rid,
    });
  };

  const orgPreview =
    orgKlsId !== "" && typeof orgKlsId === "number" ? orgKlsId : null;
  const rolePreview =
    roleKlsId !== "" && typeof roleKlsId === "number" ? roleKlsId : null;

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-xs text-muted-foreground max-w-xl leading-relaxed">
          Дополнительные связи с организациями. Основная организация задаётся в карточке объекта (
          <code className="rounded bg-muted px-1 text-[11px]">organization_id</code>
          ).
        </p>
        <Button
          type="button"
          size="sm"
          className="shrink-0 w-full sm:w-auto"
          onClick={openAddSheet}
        >
          <Plus className="h-4 w-4 mr-2" />
          Добавить
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground py-4">Загрузка связей…</p>
      ) : links.length === 0 ? (
        <p className="text-xs text-muted-foreground py-6 border rounded-md px-4 text-center bg-muted/20">
          Связей пока нет. Нажмите «Добавить», чтобы указать организацию и при необходимости роль.
        </p>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-14 text-xs font-semibold">№</TableHead>
                <TableHead className="text-xs font-semibold min-w-[200px]">
                  Организация
                </TableHead>
                <TableHead className="text-xs font-semibold min-w-[180px]">
                  Роль связи
                </TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((row: ObjectOrganizationLink) => {
                const orgMeta = orgIdToLabel.get(row.organization_kls_id);
                const roleMeta = row.role_kls_id
                  ? roleById.get(row.role_kls_id)
                  : undefined;
                return (
                  <TableRow key={row.id} className="align-top">
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.id}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium leading-snug">
                          {orgMeta?.primary ?? (
                            <span className="text-muted-foreground italic">
                              загрузка…
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          <Badge variant="secondary" className="text-[10px] font-mono">
                            id {row.organization_kls_id}
                          </Badge>
                          {orgMeta ? (
                            <span className="text-[11px] text-muted-foreground">
                              код {orgMeta.code}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.role_kls_id == null ? (
                        <span className="text-muted-foreground text-xs">Без роли</span>
                      ) : (
                        <div className="space-y-1">
                          <div className="font-medium leading-snug">
                            {roleMeta?.name ?? roleMeta?.name_short ?? "—"}
                          </div>
                          <div className="flex flex-wrap gap-1 items-center">
                            <Badge variant="outline" className="text-[10px] font-mono">
                              id {row.role_kls_id}
                            </Badge>
                            {roleMeta?.code ? (
                              <span className="text-[11px] text-muted-foreground">
                                {roleMeta.code}
                              </span>
                            ) : null}
                          </div>
                          {roleMeta?.description ? (
                            <p className="text-[11px] text-muted-foreground line-clamp-2">
                              {roleMeta.description}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setLinkIdToDelete(row.id)}
                            aria-label="Удалить связь"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Удалить связь</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side="right"
          className={cn(
            "w-full sm:max-w-lg flex h-full min-h-0 max-h-[100dvh] flex-col gap-0 p-0",
            "overflow-hidden",
          )}
        >
          <div className="shrink-0 p-6 pb-4 border-b space-y-1 bg-background">
            <SheetHeader className="space-y-2 p-0">
              <SheetTitle>Новая связь</SheetTitle>
              <SheetDescription>
                Выберите организацию из справочника KLS и при необходимости роль связи (собственник,
                УК, арендатор и т.д.). Роль можно не указывать.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-6">
            <div
              className={cn(
                "rounded-lg border p-4 space-y-3",
                orgPreview && "ring-1 ring-primary/25 border-primary/30",
              )}
            >
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary shrink-0" aria-hidden />
                <Label className="text-sm font-semibold">Организация</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground rounded"
                      aria-label="Справка"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs" side="top">
                    Юрлицо из классификатора организаций; список с отступами отражает иерархию по
                    рубрикатору.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">Обязательное поле</p>
              <OrganizationKlsCombobox
                value={orgKlsId}
                onValueChange={setOrgKlsId}
                allowEmpty={false}
                emptyPlaceholder="Выберите организацию…"
                className="min-h-10"
              />
              {orgPreview ? (
                <div className="flex flex-wrap gap-1.5 items-center pt-1">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    kls_id
                  </span>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {orgPreview}
                  </Badge>
                </div>
              ) : null}
            </div>

            <div
              className={cn(
                "rounded-lg border p-4 space-y-3",
                rolePreview && "ring-1 ring-muted-foreground/20",
              )}
            >
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                <Label className="text-sm font-semibold">Роль в связи</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground rounded"
                      aria-label="Справка по роли"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs" side="top">
                    Не путать с ролью пользователя в системе. Это роль организации по отношению к
                    объекту.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">Необязательно</p>
              <OrganizationRoleKlsCombobox
                value={roleKlsId}
                onValueChange={setRoleKlsId}
                className="min-h-10"
              />
              {rolePreview ? (
                <div className="flex flex-wrap gap-1.5 items-center pt-1">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    kls_id
                  </span>
                  <Badge variant="outline" className="font-mono text-xs">
                    {rolePreview}
                  </Badge>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  Можно оставить без роли — будет только привязка организации.
                </p>
              )}
            </div>
          </div>

          <SheetFooter
            className={cn(
              "shrink-0 flex-col sm:flex-row gap-2 sm:justify-end sm:space-x-0",
              "border-t bg-background p-6 pt-4",
              "pb-[max(1rem,env(safe-area-inset-bottom,0px))]",
              "shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.08)]",
            )}
          >
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => handleSheetOpenChange(false)}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={handleAdd}
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? "Сохранение…" : "Сохранить связь"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={linkIdToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setLinkIdToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтверждение удаления</AlertDialogTitle>
            <AlertDialogDescription>
              Удалить связь объекта с организацией? Это действие нельзя отменить. Запись будет
              удалена из списка дополнительных связей (основная организация объекта в карточке не
              меняется).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteLink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
